import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { PageHeader } from "../../components/StaffLayout";
import { Modal, Inp, Sel } from "./Enquiries";
import { Plus, Calendar, UserPlus, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Appointments() {
  const [items, setItems] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const [a, p, d] = await Promise.all([api.get("/appointments"), api.get("/patients"), api.get("/doctors")]);
    setItems(a.data);
    setPatients(p.data);
    setDoctors(d.data);
  };

  useEffect(() => {
    load();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const unassigned = useMemo(() => items.filter((a) => a.assignment_state === "unassigned"), [items]);
  const assignedUpcoming = useMemo(
    () => items.filter((a) => a.assignment_state !== "unassigned" && a.starts_at >= today),
    [items, today]
  );
  const past = useMemo(() => items.filter((a) => a.starts_at < today), [items, today]);

  return (
    <div data-testid="appointments-page">
      <PageHeader
        title="Appointments"
        subtitle={`${unassigned.length} unassigned · ${assignedUpcoming.length} upcoming · ${past.length} past`}
        actions={
          <button
            onClick={() => setShowNew(true)}
            className="inline-flex items-center gap-2 bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm"
            data-testid="add-appointment-btn"
          >
            <Plus size={16} weight="bold" /> Schedule
          </button>
        }
      />

      <div className="px-6 sm:px-10 py-8 space-y-6">
        <Section
          title="Unassigned Requests"
          hint="Portal requests waiting for doctor assignment"
          items={unassigned}
          doctors={doctors}
          onClick={setEditing}
          onSaved={load}
          tone="pending"
        />
        <Section title="Assigned Upcoming" items={assignedUpcoming} onClick={setEditing} />
        <Section title="Past" items={past} onClick={setEditing} muted />
      </div>

      {showNew && (
        <AppointmentModal
          patients={patients}
          doctors={doctors}
          onClose={() => setShowNew(false)}
          onSaved={() => {
            setShowNew(false);
            load();
          }}
        />
      )}
      {editing && (
        <AppointmentModal
          patients={patients}
          doctors={doctors}
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function Section({ title, hint, items, onClick, muted, tone, doctors = [], onSaved }) {
  return (
    <div>
      <div className="flex items-end justify-between gap-3 mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] font-black text-zinc-700">{title}</div>
          {hint ? <div className="text-xs text-zinc-500 mt-1">{hint}</div> : null}
        </div>
        <div className="text-xs text-zinc-500">{items.length} item{items.length === 1 ? "" : "s"}</div>
      </div>
      {items.length === 0 ? (
        <div className="bg-white border border-zinc-200 rounded-sm p-8 text-center text-sm text-zinc-500">Nothing here.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {items.map((a) => (
            <AppointmentCard
              key={a.id}
              item={a}
              doctors={doctors}
              onClick={onClick}
              onSaved={onSaved}
              muted={muted}
              tone={tone}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function AppointmentCard({ item, doctors, onClick, onSaved, muted, tone }) {
  const [assigning, setAssigning] = useState(false);

  const assignSuggested = async (e) => {
    e.stopPropagation();
    if (!item.suggested_doctor?.id) return;
    setAssigning(true);
    try {
      await api.patch(`/appointments/${item.id}`, { doctor_id: item.suggested_doctor.id });
      toast.success(`Assigned to ${item.suggested_doctor.name}`);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setAssigning(false);
    }
  };

  const toneClasses =
    tone === "pending"
      ? "border-amber-200 bg-amber-50/40"
      : muted
        ? "border-zinc-200 opacity-70"
        : "border-zinc-200";

  return (
    <button
      onClick={() => onClick(item)}
      className={`bg-white border rounded-sm p-4 text-left hover:border-zinc-900 transition-colors ${toneClasses}`}
      data-testid={`appt-${item.id}`}
    >
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500">
        <Calendar size={14} weight="bold" />
        {new Date(item.starts_at).toLocaleString()}
      </div>

      <div className="mt-2 font-bold text-zinc-900">{item.patient?.name || "—"}</div>
      <div className="text-xs text-zinc-500">{item.patient?.phone}</div>

      <div className="mt-2 text-xs text-zinc-700">
        {item.treatment || "Consultation"}
        {item.doctor_obj?.name ? ` · ${item.doctor_obj.name}` : item.doctor ? ` · ${item.doctor}` : ""}
      </div>

      {item.assignment_state === "unassigned" ? (
        <div className="mt-3 space-y-2">
          <div className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded-sm">
            <UserPlus size={12} weight="bold" /> Unassigned
          </div>
          {item.suggested_doctor ? (
            <div className="border border-zinc-200 rounded-sm p-2 bg-white">
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-500 inline-flex items-center gap-1">
                <Sparkle size={12} weight="bold" /> Suggested doctor
              </div>
              <div className="text-sm font-semibold text-zinc-900 mt-1">{item.suggested_doctor.name}</div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-zinc-400 mt-2">Why suggested</div>
              <div className="text-xs text-zinc-600 mt-1">{item.suggested_doctor.reason}</div>
              <button
                onClick={assignSuggested}
                disabled={assigning}
                className="mt-2 text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]"
                data-testid={`assign-suggested-${item.id}`}
              >
                {assigning ? "Assigning..." : "Assign suggested doctor"}
              </button>
            </div>
          ) : (
            <div className="text-xs text-zinc-500">No suggestion available yet.</div>
          )}
        </div>
      ) : (
        <div className="mt-3">
          <span
            className={`text-[10px] uppercase tracking-[0.2em] font-bold ${
              item.status === "scheduled"
                ? "text-blue-600"
                : item.status === "completed"
                  ? "text-green-600"
                  : item.status === "cancelled"
                    ? "text-red-600"
                    : "text-zinc-500"
            }`}
          >
            {item.status}
          </span>
        </div>
      )}
    </button>
  );
}

function AppointmentModal({ item, patients, doctors = [], onClose, onSaved }) {
  const isEdit = !!item;
  const [form, setForm] = useState({
    patient_id: item?.patient_id || patients[0]?.id || "",
    starts_at: item?.starts_at ? item.starts_at.slice(0, 16) : new Date(Date.now() + 86400000).toISOString().slice(0, 16),
    duration_min: item?.duration_min || 30,
    treatment: item?.treatment || "",
    doctor_id: item?.doctor_id || "",
    status: item?.status || "scheduled",
    notes: item?.notes || "",
  });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        doctor_id: form.doctor_id || null,
        duration_min: Number(form.duration_min) || 30,
        starts_at: new Date(form.starts_at).toISOString(),
      };
      if (isEdit) await api.patch(`/appointments/${item.id}`, payload);
      else await api.post("/appointments", payload);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this appointment?")) return;
    await api.delete(`/appointments/${item.id}`);
    toast.success("Deleted");
    onSaved();
  };

  return (
    <Modal onClose={onClose} title={isEdit ? "Edit appointment" : "Schedule appointment"}>
      {item?.assignment_state === "unassigned" && item?.suggested_doctor ? (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-sm p-3">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700">Suggested doctor</div>
          <div className="text-sm font-semibold text-zinc-900 mt-1">{item.suggested_doctor.name}</div>
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-amber-700/70 mt-2">Why suggested</div>
          <div className="text-xs text-zinc-600 mt-1">{item.suggested_doctor.reason}</div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Patient</label>
          <select
            value={form.patient_id}
            onChange={(e) => setForm({ ...form, patient_id: e.target.value })}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900"
            data-testid="appt-patient"
          >
            <option value="">— select —</option>
            {patients.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name} ({p.phone})
              </option>
            ))}
          </select>
        </div>
        <Inp label="Date & time" type="datetime-local" v={form.starts_at} onChange={(v) => setForm({ ...form, starts_at: v })} testid="appt-datetime" />
        <Inp label="Duration (min)" type="number" v={form.duration_min} onChange={(v) => setForm({ ...form, duration_min: v })} testid="appt-duration" />
        <Inp label="Treatment" v={form.treatment} onChange={(v) => setForm({ ...form, treatment: v })} testid="appt-treatment" />
        <div>
          <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Doctor</label>
          <select
            value={form.doctor_id}
            onChange={(e) => setForm({ ...form, doctor_id: e.target.value })}
            className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 bg-white focus:outline-none focus:border-zinc-900"
            data-testid="appt-doctor"
          >
            <option value="">— keep unassigned —</option>
            {doctors.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
        </div>
        <Sel
          label="Status"
          v={form.status}
          onChange={(v) => setForm({ ...form, status: v })}
          opts={[
            ["scheduled", "Scheduled"],
            ["completed", "Completed"],
            ["cancelled", "Cancelled"],
            ["no_show", "No-show"],
          ]}
          testid="appt-status"
        />
      </div>
      <div className="mt-3">
        <label className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-700">Notes</label>
        <textarea
          rows={3}
          value={form.notes}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
          className="mt-2 w-full border border-zinc-300 rounded-sm px-3 py-2 focus:outline-none focus:border-zinc-900"
          data-testid="appt-notes"
        />
      </div>
      <div className="mt-5 flex items-center justify-between">
        {isEdit ? (
          <button onClick={remove} className="text-xs uppercase tracking-[0.2em] font-bold text-[#E60000]" data-testid="appt-delete">
            Delete
          </button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <button onClick={onClose} className="bg-transparent border border-zinc-900 text-zinc-900 font-semibold rounded-sm px-4 py-2 text-sm">
            Cancel
          </button>
          <button
            onClick={save}
            disabled={busy}
            className="bg-[#E60000] hover:bg-[#CC0000] text-white font-semibold rounded-sm px-4 py-2 text-sm disabled:opacity-60"
            data-testid="appt-save"
          >
            {busy ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}
